import type { ConfKodFolie } from '@eliadmin/core/src/api/proform/proform-types';
import { LightboxComponent } from '@eliadmin/core/src/components/lightbox/lightbox-component';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { useEliProductDetails } from '@eliadmin/core/src/hooks/proform-eli-product-detail';
import { useProductColorVariants } from '@eliadmin/core/src/hooks/proform-product-color-variants';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  extractProformArray,
  getProformImageUrl,
  parseProformNumber,
} from '@eliadmin/core/src/utils/proform-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { FiDownload } from 'react-icons/fi';

export function ProductTabColorVariants() {
  const product = useVariantsStore(s => s.product);

  const variants = useProductColorVariants(product.code);
  const detail = useEliProductDetails(product.code);
  const colorVariants = useMemo(() => (variants.data ?? []).filter(Boolean), [variants.data]);

  const [selectedCode, setSelectedCode] = useState<string | null>(
    colorVariants[0]?.kod_grp ?? null,
  );

  const { t } = useTranslations();

  useEffect(() => {
    if (!selectedCode) {
      setSelectedCode(colorVariants[0]?.kod_grp ?? null);
    }
  }, [selectedCode, colorVariants]);

  const variantsByCode = useMemo(() => {
    const result: Record<string, ConfKodFolie[]> = {};

    for (const variant of colorVariants) {
      if (!variant?.kod_grp) {
        continue;
      }

      if (!result[variant.kod_grp]) {
        result[variant.kod_grp] = [];
      }

      result[variant.kod_grp].push(variant);
    }

    return result;
  }, [colorVariants]);

  const orderedGroups = useMemo(() => {
    const rawTechInfo = detail.data?.root?.smako_tech_info?.smako_tech_info;
    const detailGroups = extractProformArray(rawTechInfo)
      .filter(item => item.kod?.startsWith('folie_'))
      .map(item => ({
        ...item,
        kod: item.kod.replace('folie_', ''),
      }));

    const detailByCode = new Map(detailGroups.map(item => [item.kod, item]));
    const codes = Object.keys(variantsByCode);

    const result = codes.map(kod => {
      const detailItem = detailByCode.get(kod);
      return (
        detailItem ?? {
          kod,
          popis: kod,
          poradi_zobrazeni: '',
        }
      );
    });

    result.sort((a, b) => {
      const numA = parseProformNumber(a.poradi_zobrazeni);
      const numB = parseProformNumber(b.poradi_zobrazeni);
      const safeA = Number.isFinite(numA) ? numA : Number.MAX_SAFE_INTEGER;
      const safeB = Number.isFinite(numB) ? numB : Number.MAX_SAFE_INTEGER;
      return safeA - safeB || a.kod.localeCompare(b.kod);
    });

    return result;
  }, [detail.data?.root?.smako_tech_info?.smako_tech_info, variantsByCode]);

  const visibleVariants = useMemo(
    () => (selectedCode ? (variantsByCode[selectedCode] ?? []) : colorVariants),
    [selectedCode, variantsByCode, colorVariants],
  );

  const [isOpen, setIsOpen] = useState(false);

  const lightBoxImages = useMemo(() => {
    return visibleVariants
      .filter(variant => !!variant?.pic)
      .map(variant => {
        return {
          url: getProformImageUrl(variant.pic),
          alt: variant.kod,
        };
      });
  }, [visibleVariants]);

  const [orderedLightBoxImages, setOrderedLightBoxImages] = useState(lightBoxImages);

  const handleOpenLightbox = (index: number) => {
    if (!lightBoxImages[index]) {
      return;
    }

    const newImages = [
      lightBoxImages[index],
      ...lightBoxImages.slice(index + 1),
      ...lightBoxImages.slice(0, index),
    ];
    setOrderedLightBoxImages(newImages);
    setIsOpen(true);
  };

  const handleDownload = (url: string, filename: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Container
      className={cn('flex flex-col gap-4 px-2 @container', 'ea-product-detail-tab-color-variants')}
    >
      <LightboxComponent
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        images={orderedLightBoxImages}
        zoom={{ maxZoomPixelRatio: 10, zoom: 5, scrollToZoom: true } as any}
      />
      <h2 className="mb-4 mt-4 text-base font-bold text-muted-foreground">{t('text1046')}</h2>

      <div className="flex flex-row flex-wrap gap-2">
        {orderedGroups.map(group => {
          return (
            <div
              key={group.kod}
              className={cn(
                'flex cursor-pointer items-center justify-center rounded-full border border-primary px-4 py-2 text-primary transition-colors',
                selectedCode === group.kod
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-primary-foreground',
                'hover:bg-primary/80 hover:text-primary-foreground ',
              )}
              onClick={() => setSelectedCode(group.kod)}
            >
              {group.kod}
            </div>
          );
        })}
      </div>

      <div className="flex flex-row flex-wrap gap-1">
        {visibleVariants.map(variant => {
          if (!variant) {
            return null;
          }

          const lightboxIndex = lightBoxImages.findIndex(
            image => image.url === (variant.pic ? getProformImageUrl(variant.pic) : ''),
          );

          return (
            <Fragment key={variant.kod}>
              <div
                className="group relative flex h-[150px] w-[150px] cursor-pointer flex-col items-start justify-start gap-4 overflow-hidden rounded-xl border border-border shadow-sm transition-all hover:z-10 hover:scale-110 hover:border-muted-foreground hover:shadow-2xl"
                onClick={() => handleOpenLightbox(lightboxIndex)}
              >
                {variant.pic && (
                  <>
                    <FiDownload
                      className="absolute right-1 top-1 h-6 w-6 cursor-pointer rounded-sm bg-primary p-[2px] text-primary-foreground opacity-0 transition-all hover:bg-primary-foreground hover:text-primary group-hover:opacity-100"
                      onClick={(e: any) => {
                        e.stopPropagation();
                        handleDownload(getProformImageUrl(variant.pic), `${variant.kod}.jpg`);
                      }}
                    />
                    <Image
                      src={getProformImageUrl(variant.pic)}
                      alt={variant.kod}
                      width={150}
                      height={150}
                      sizes="100vw"
                      className="h-auto w-full object-contain"
                    />
                  </>
                )}
                <div
                  className={cn(
                    'absolute bottom-0 flex w-full cursor-pointer flex-col items-center justify-end gap-0 rounded-t-xl bg-primary p-2 text-primary-foreground opacity-0 transition-opacity duration-300',
                    'group-hover:opacity-90',
                  )}
                >
                  <h6 className="text-center text-xs font-bold">{variant.kod}</h6>
                  <h4 className="text-center text-xs font-medium">{variant.popis}</h4>
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>
    </Container>
  );
}
